/**
 * =============================================================================
 * WaterPipeline - Brillo de agua tipo juego 2D (líneas de luz que derivan)
 * =============================================================================
 *
 * Render pipeline custom (SinglePipeline) que se aplica a cada sprite de bloque
 * de agua con `sprite.setPipeline(WATER_PIPELINE_KEY)`. Dibuja un brillo ENCIMA
 * del agua sin tocar la textura: la técnica estándar de agua 2D top-down
 * (líneas de luz onduladas + threshold alto → pocos destellos que derivan), ver
 * https://www.cyanilux.com/tutorials/2d-water-shader-breakdown/ .
 *
 * Por qué un pipeline y no un overlay aparte:
 *  - Se dibuja SOLO donde hay agua (es el pipeline del propio sprite de agua),
 *    nunca sobre el pasto/costa.
 *  - TODOS los bloques de agua comparten la misma textura de frame en cada
 *    instante (timer global), así que se batchean en UN draw call → ~gratis.
 *    (El intento viejo de `add.shader` por tile frenaba el pipeline y colgaba.)
 *  - El brillo se calcula en coords de MUNDO (uWorldView + gl_FragCoord), así es
 *    continuo entre bloques de 128px (sin grilla) y queda anclado al agua al
 *    caminar (no "nada").
 *
 * Uniforms por frame (`uTime`, `uWorldView`) los setea GameScene en su update()
 * vía `setFrame()`. `uResolution`/`uMainSampler` los inicializa SinglePipeline.boot.
 */

import Phaser from 'phaser';

/** Key con la que se registra el pipeline en el PipelineManager. */
export const WATER_PIPELINE_KEY = 'Water';

const FRAG_SHADER = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform sampler2D uMainSampler;
uniform vec2 uResolution;
uniform vec4 uWorldView; // x, y, width, height del worldView de la cámara
uniform float uTime;

varying vec2 outTexCoord;
varying float outTintEffect;
varying vec4 outTint;

// ============================ Tuning ============================
// Escala de ola: px de mundo -> unidades de ola. Más grande = olas más chicas.
const float WAVE_SCALE   = 0.10;
const float NORMAL_STR   = 0.16;    // fuerza de la pendiente (más alto = más choppy)
const int   ITER_GEO     = 3;       // octavas del heightfield
const float SEA_HEIGHT   = 0.6;
const float SEA_CHOPPY   = 2.2;
const float SEA_FREQ     = 0.16;
const float SEA_SPEED    = 0.9;     // velocidad del oleaje
const float REFLECT_STR  = 0.6;     // fuerza del reflejo de cielo
const float SPEC_STR     = 0.45;    // fuerza del destello especular
const float HEIGHT_TINT  = 0.08;    // claror de las crestas
const vec3  SEA_BASE        = vec3(0.04, 0.16, 0.28);       // azul profundo
const vec3  SEA_WATER_COLOR = vec3(0.32, 0.62, 0.66);       // tinte del agua
const vec3  SHALLOW_COLOR   = vec3(0.24, 0.80, 0.85);       // cyan de bajío (costa)
const float SHALLOW_STR     = 0.3;                          // cuánto vira a cyan en la orilla
const float SHALLOW_MAX_CALM = 0.5;                         // cyan solo donde calm < esto (más chico = franja más corta)
const vec3  SUN_DIR  = vec3(0.0, 0.78, 0.62);               // dirección del sol (y = arriba)
const vec3  EYE_DIR  = vec3(0.0, -1.0, 0.30);              // vista cenital: mira hacia abajo (-y), apenas inclinada
const mat2  OCT_M = mat2(1.6, 1.2, -1.2, 1.6);
// ================================================================

// Hash/noise estilo Seascape (en highp aguanta coords de mundo escaladas chicas).
float hash (vec2 p) {
    float h = dot(p, vec2(127.1, 311.7));
    return fract(sin(h) * 43758.5453123);
}
float noise (in vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return -1.0 + 2.0 * mix(
        mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),
        mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}

float diffuse (vec3 n, vec3 l, float p) { return pow(dot(n, l) * 0.4 + 0.6, p); }
float specular (vec3 n, vec3 l, vec3 e, float s) {
    float nrm = (s + 8.0) / (3.14159 * 8.0);
    return pow(max(dot(reflect(e, n), l), 0.0), s) * nrm;
}
vec3 getSkyColor (vec3 e) {
    e.y = (max(e.y, 0.0) * 0.8 + 0.2) * 0.8;
    return vec3(pow(1.0 - e.y, 2.0), 1.0 - e.y, 0.6 + (1.0 - e.y) * 0.4) * 1.1;
}

// Una octava de ola (Seascape).
float sea_octave (vec2 uv, float choppy) {
    uv += noise(uv);
    vec2 wv = 1.0 - abs(sin(uv));
    vec2 swv = abs(cos(uv));
    wv = mix(wv, swv, wv);
    return pow(1.0 - pow(wv.x * wv.y, 0.65), choppy);
}

// Heightfield del océano en el plano (x,y) de mundo. 'st' = uTime * SEA_SPEED.
float oceanHeight (vec2 p, float st) {
    float freq = SEA_FREQ;
    float amp = SEA_HEIGHT;
    float choppy = SEA_CHOPPY;
    vec2 uv = p; uv.x *= 0.75;
    float h = 0.0;
    for (int i = 0; i < ITER_GEO; i++) {
        float d = sea_octave((uv + st) * freq, choppy);
        d += sea_octave((uv - st) * freq, choppy);
        h += d * amp;
        uv = OCT_M * uv; freq *= 1.9; amp *= 0.22;
        choppy = mix(choppy, 1.0, 0.2);
    }
    return h;
}

void main () {
    // Posición de mundo del fragmento (continua entre bloques → sin grilla,
    // anclada al mundo → no "nada" al mover la cámara).
    vec2 frac = vec2(gl_FragCoord.x / uResolution.x, 1.0 - gl_FragCoord.y / uResolution.y);
    vec2 world = uWorldView.xy + frac * uWorldView.zw;
    vec2 p = world * WAVE_SCALE;
    float st = 1.0 + uTime * SEA_SPEED;

    // Factor de "calma" por bloque (1 = mar abierto, 0 = costa). Lo setea GameScene
    // en el tint (gris) del sprite: outTint.r = factor. Atenúa olas/brillo en la costa.
    float calm = outTint.r;

    // Heightfield + normal (diferencias finitas en el plano). Convención y = arriba.
    float e = 0.06;
    float h  = oceanHeight(p, st);
    float hx = oceanHeight(p + vec2(e, 0.0), st);
    float hy = oceanHeight(p + vec2(0.0, e), st);
    vec3 n = normalize(vec3(
        -(hx - h) / e * NORMAL_STR * calm,
        1.0,
        -(hy - h) / e * NORMAL_STR * calm));

    vec3 eye = normalize(EYE_DIR);
    vec3 sun = normalize(SUN_DIR);

    // Color de mar (Seascape): fresnel mezcla reflejo de cielo y refracción + specular.
    float fresnel = clamp(1.0 - dot(n, -eye), 0.0, 1.0);
    fresnel = min(pow(fresnel, 3.0), 0.5);

    vec3 reflected = getSkyColor(reflect(eye, n)) * REFLECT_STR;
    vec3 refracted = SEA_BASE + diffuse(n, sun, 80.0) * SEA_WATER_COLOR * 0.12;
    vec3 oceanCol = mix(refracted, reflected, fresnel);

    // Tinte por altura (crestas más claras) + specular (los destellos que viajan).
    // Ambos atenuados por calm para que la costa quede plana/tranquila.
    oceanCol += SEA_WATER_COLOR * (h - SEA_HEIGHT) * HEIGHT_TINT * calm;
    oceanCol += vec3(specular(n, sun, eye, 60.0)) * SPEC_STR * calm;

    // Bajío: cerca de la costa (calm→0) el agua vira a cyan claro; se apaga del todo en
    // calm >= SHALLOW_MAX_CALM (recorta el LARGO de la franja hacia el mar). El cuadrado
    // concentra el degradé en la orilla, así se lee que ahí es más bajito.
    float shallow = clamp((SHALLOW_MAX_CALM - calm) / SHALLOW_MAX_CALM, 0.0, 1.0);
    shallow = shallow * shallow;
    oceanCol = mix(oceanCol, SHALLOW_COLOR, shallow * SHALLOW_STR);

    // Salida: reemplaza el color del agua. alpha = forma del bloque. NOTA: el tint
    // se usa como dato (calm), por eso NO multiplicamos el color por outTint.
    vec4 texture = texture2D(uMainSampler, outTexCoord);
    gl_FragColor = vec4(oceanCol, texture.a);
}
`;

/** Océano procedural (adaptación de "Seascape"). */
export class WaterPipeline extends Phaser.Renderer.WebGL.Pipelines.SinglePipeline {
  constructor(game: Phaser.Game) {
    super({ game, fragShader: FRAG_SHADER });
  }

  /**
   * Actualiza los uniforms por frame. Llamar desde GameScene.update() con el
   * tiempo (segundos) y el worldView de la cámara principal.
   */
  setFrame(timeSeconds: number, worldView: Phaser.Geom.Rectangle): void {
    this.set1f('uTime', timeSeconds);
    this.set4f('uWorldView', worldView.x, worldView.y, worldView.width, worldView.height);
  }
}
